import { useState, useEffect, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { fetchClinic, fetchLocations, fetchLocationsByClinic, fetchRooms, fetchDoctors } from './apiCalls';

export function useClinicData() {
  const { user, token } = useAuth();

  const [clinics, setClinics] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedClinic, setSelectedClinic] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [doctors, setDoctors] = useState([]);

  // Fetch clinics & locations when user or token changes
  // Super admins - load all Clinics
  // Admin - only load assigned clinic
  useEffect(() => {
    if (!user || !token) return;

    if (user.role === 'super_admin') {
      fetchClinic(user, token, setClinics);
      fetchLocations(token, setLocations);
    } else {
      setClinics([{ clinic_id: user.clinic_id }]);
      setSelectedClinic(user.clinic_id);
      fetchLocationsByClinic(user.clinic_id, token, setLocations);
    }

  }, [user, token]);

  // Fetch locations when clinic changes
  useEffect(() => {
    if (!selectedClinic || !token) return;

    fetchLocationsByClinic(selectedClinic, token, setLocations);
    setSelectedLocation(null);
  }, [selectedClinic, token]);

  // Fetch rooms & doctors when location changes
  useEffect(() => {
    if (!selectedLocation || !token) return;

    fetchRooms(selectedLocation.location_id, token, setRooms);
    fetchDoctors(selectedLocation.location_id, token, [], setDoctors);
  }, [selectedLocation, token]);

  const filteredLocations = useMemo(() => {
    if (!selectedClinic) return [];
    return locations.filter((loc) => loc.clinic_id === parseInt(selectedClinic, 10));
  }, [selectedClinic, locations]);

  const handleLocationChange = (locationId) => {
    const location = locations.find((loc) => loc.location_id === parseInt(locationId, 10));
    setSelectedLocation(location || null);
  };

  const refreshDoctors = async() => {
    if (!selectedLocation || !token) return;

    try {
      await fetchDoctors(selectedLocation.location_id, token, [], setDoctors);
    } catch (error) {
      console.error('Failed to refresh doctors', error);
    }
  }

  return {
    clinics,
    locations,
    selectedClinic,
    setSelectedClinic: user?.role === 'super_admin' ? setSelectedClinic : () => {},
    selectedLocation,
    setSelectedLocation,
    filteredLocations,
    handleLocationChange,
    rooms,
    doctors,
    refreshDoctors,
  };
}
